/**
 * Platform Protected Route
 *
 * Protects platform routes by checking platform auth state.
 * Redirects to platform login if not authenticated.
 */
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { platformGetMe, platformRefresh } from '@/lib/platform-api';
import { usePlatformAuthStore } from '@/stores/platform-auth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireSuperAdmin?: boolean;
}

export function PlatformProtectedRoute({
  children,
  requireSuperAdmin = false,
}: ProtectedRouteProps) {
  const location = useLocation();
  const {
    admin,
    accessToken,
    isAuthenticated,
    isLoading,
    setAdmin,
    setAccessToken,
    setLoading,
    clearAuth,
  } = usePlatformAuthStore();

  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      // If we have an admin, we're good
      if (admin && accessToken) {
        setIsChecking(false);
        setLoading(false);
        return;
      }

      // If we have a token but no admin, fetch admin info
      if (accessToken && !admin) {
        try {
          const response = await platformGetMe();
          if (response.data) {
            setAdmin({
              id: response.data.id,
              email: response.data.email,
              name: response.data.name,
              organizationId: response.data.organizationId,
              isSuperAdmin: response.data.isSuperAdmin,
              isPrimary: response.data.isPrimary,
              activeSessions: response.data.activeSessions,
            });
          }
        } catch {
          clearAuth();
        }
        setIsChecking(false);
        setLoading(false);
        return;
      }

      // Try to refresh token (cookie-based)
      try {
        const response = await platformRefresh();
        if (response.data) {
          setAccessToken(response.data.accessToken);
          setAdmin({
            id: response.data.admin.id,
            email: response.data.admin.email,
            name: response.data.admin.name,
            organizationId: response.data.admin.organizationId,
            isSuperAdmin: response.data.admin.isSuperAdmin,
            isPrimary: response.data.admin.isPrimary,
          });
        } else {
          clearAuth();
        }
      } catch {
        clearAuth();
      }

      setIsChecking(false);
      setLoading(false);
    };

    checkAuth();
  }, [accessToken, admin, clearAuth, setAccessToken, setAdmin, setLoading]);

  // Show loading state
  if (isChecking || isLoading) {
    return <PlatformLoadingScreen />;
  }

  // Not authenticated - redirect to login
  if (!(isAuthenticated && admin)) {
    return <Navigate to="/platform/login" state={{ from: location }} replace />;
  }

  // Check super admin requirement
  if (requireSuperAdmin && !admin.isSuperAdmin) {
    return <Navigate to="/platform/dashboard" replace />;
  }

  return <>{children}</>;
}

/**
 * Loading screen matching the platform aesthetic
 */
function PlatformLoadingScreen() {
  return (
    <div className="platform-loading">
      <div className="platform-loading__grid" />
      <div className="platform-loading__content">
        <div className="platform-loading__spinner">
          <svg viewBox="0 0 50 50">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="31.4 31.4"
            />
          </svg>
        </div>
        <div className="platform-loading__text">INITIALIZING</div>
      </div>

      <style>{`
        .platform-loading {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0A0F1C;
          position: relative;
        }

        .platform-loading__grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(0, 229, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 229, 255, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
        }

        .platform-loading__content {
          position: relative;
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
        }

        .platform-loading__spinner {
          width: 50px;
          height: 50px;
          color: #00E5FF;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          100% { transform: rotate(360deg); }
        }

        .platform-loading__text {
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 3px;
          color: rgba(255, 255, 255, 0.5);
        }
      `}</style>
    </div>
  );
}

export default PlatformProtectedRoute;
